import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useTranslation } from "react-i18next";
import { Asset } from "@/types/assets";
import { AppleButton } from "@/components/ui/apple-button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ShareDialog } from "@/components/shared/ShareDialog";
import { AssetRecordsList } from "@/components/assets/AssetRecordsList";
import { cn, getStatusColor, formatDate } from "@/lib/utils";
import { QRCodeSVG } from "qrcode.react";
import {
  Building2,
  MapPin,
  Share2,
  Pencil,
  ImageIcon,
  FileText,
  ArrowLeft,
  DollarSign,
  Phone,
  User,
  Calendar,
  Hash,
  QrCode,
  Nfc,
  Key,
  Info,
  History,
  TrendingUp,
  Clock,
  Activity,
  AlertTriangle,
  CheckCircle2,
  BarChart3,
  PieChart,
  Link as LinkIcon,
  Printer,
  Download,
  Network,
  Wrench,
  Gauge,
  CalendarClock,
  Zap,
  ChevronLeft,
  ChevronRight,
  Star,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useState, useMemo } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart as RePieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
} from 'recharts';
import { Button } from "@/components/ui/button";
import { AssetRecordAttachments } from "@/components/assets/AssetRecordAttachments";

interface PreviewState {
  isOpen: boolean;
  type: 'image' | 'document';
  url: string;
  title: string;
}

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042'];

interface RecordAttachment {
  id: number;
  title: string;
  images: string | null;
  documents: string | null;
  assets: {
    asset_name: string;
    name: string;
  };
}

interface DatabaseRecordAttachment {
  id: number;
  title: string;
  images: string | null;
  documents: string | null;
  assets: {
    asset_name: string;
    name: string;
  };
}

// Helper function to process attachment string
const processAttachmentString = (value: string | null): string[] => {
  if (!value) return [];
  return value.split(',').filter(Boolean);
};

export default function AssetView() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [previewState, setPreviewState] = useState<PreviewState | null>(null);

  // Add pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  const { data: asset, isLoading } = useQuery<Asset>({
    queryKey: ["asset", id],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("User not authenticated");

      const { data, error } = await supabase
        .from("assets")
        .select(`
          *,
          asset_records (
            *
          ),
          shared_assets (
            shared_with
          ),
          company:companies!company_id (
            id,
            name,
            description
          ),
          place:places!place_id (
            id,
            name,
            description
          )
        `)
        .eq("id", parseInt(id || "0"))
        .single();

      if (error) throw error;
      return {
        ...data,
        company: data.company?.[0],
        place: data.place?.[0],
        asset_records: data.asset_records,
        shared_assets: data.shared_assets
      } as Asset;
    },
  });

  // New query for timeline data
  const { data: timelineData } = useQuery({
    queryKey: ["asset-timeline", id],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("User not authenticated");

      // Fetch all records and sort by date
      const { data: records } = await supabase
        .from("asset_records")
        .select("*")
        .eq("asset_id", parseInt(id || "0"))
        .order("created_at", { ascending: false });

      return records || [];
    },
  });

  // New query for analytics data
  const { data: analyticsData } = useQuery({
    queryKey: ["asset-analytics", id],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("User not authenticated");

      // Fetch all records with costs
      const { data: recordsWithCosts } = await supabase
        .from("asset_records")
        .select("*")
        .eq("asset_id", parseInt(id || "0"))
        .order("created_at");

      // Calculate monthly costs
      const monthlyCosts = recordsWithCosts?.reduce((acc: any, record) => {
        const month = new Date(record.created_at).toLocaleString('default', { month: 'long', year: 'numeric' });
        acc[month] = (acc[month] || 0) + (record.cost || 0);
        return acc;
      }, {});

      // Calculate status distribution
      const statusDistribution = recordsWithCosts?.reduce((acc: any, record) => {
        acc[record.status] = (acc[record.status] || 0) + 1;
        return acc;
      }, {});

      return {
        monthlyCosts: Object.entries(monthlyCosts || {}).map(([name, value]) => ({ name, value })),
        statusDistribution: Object.entries(statusDistribution || {}).map(([name, value]) => ({ name, value })),
      };
    },
  });

  // Query for related assets
  const { data: relatedAssets } = useQuery({
    queryKey: ["related-assets", id],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("User not authenticated");

      // Fetch assets in the same category or location
      const { data: assets } = await supabase
        .from("assets")
        .select(`
          id,
          name,
          asset_name,
          category,
          status,
          images
        `)
        .neq("id", parseInt(id || "0"))
        .or(`category.eq.${asset?.category},place_id.eq.${asset?.place_id}`)
        .limit(6);

      return assets || [];
    },
    enabled: !!asset,
  });

  // New query for maintenance schedule
  const { data: maintenanceSchedule } = useQuery({
    queryKey: ["maintenance-schedule", id],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("User not authenticated");

      const { data: records } = await supabase
        .from("asset_records")
        .select("*")
        .eq("asset_id", parseInt(id || "0"))
        .eq("type", "Maintenance")
        .order("created_at", { ascending: false })
        .limit(5);

      return records || [];
    },
  });

  // Calculate performance metrics
  const performanceMetrics = useMemo(() => {
    if (!timelineData) return null;

    const now = new Date();
    const thirtyDaysAgo = new Date(now.setDate(now.getDate() - 30));

    // Filter records from last 30 days
    const recentRecords = timelineData.filter(
      record => new Date(record.created_at) > thirtyDaysAgo
    );

    // Calculate metrics
    const totalIncidents = recentRecords.filter(r => r.type === 'Incident').length;
    const totalMaintenances = recentRecords.filter(r => r.type === 'Maintenance').length;
    const uptime = recentRecords.filter(r => r.status !== 'Down' && r.status !== 'Maintenance').length / recentRecords.length;
    const efficiency = 1 - (totalIncidents / (recentRecords.length || 1));

    return {
      uptime: Math.round(uptime * 100),
      efficiency: Math.round(efficiency * 100),
      incidents: totalIncidents,
      maintenances: totalMaintenances,
    };
  }, [timelineData]);

  // Add pagination for timeline data
  const paginatedTimelineData = useMemo(() => {
    if (!timelineData) return [];
    const startIndex = (currentPage - 1) * pageSize;
    return timelineData.slice(startIndex, startIndex + pageSize);
  }, [timelineData, currentPage, pageSize]);

  const timelinePaginationMetadata = useMemo(() => {
    if (!timelineData) return null;
    const totalCount = timelineData.length;
    const totalPages = Math.ceil(totalCount / pageSize);
    const hasMore = currentPage * pageSize < totalCount;

    return {
      currentPage,
      pageSize,
      totalCount,
      totalPages,
      hasMore
    };
  }, [timelineData, currentPage, pageSize]);

  const handlePreviousPage = () => {
    setCurrentPage((prev) => Math.max(1, prev - 1));
  };

  const handleNextPage = () => {
    if (timelinePaginationMetadata?.hasMore) {
      setCurrentPage((prev) => prev + 1);
    }
  };

  const handlePrintQR = () => {
    const canvas = document.getElementById('asset-qr-code') as HTMLCanvasElement;
    const printWindow = window.open('', '', 'width=800,height=600');
    if (printWindow) {
      printWindow.document.write(`
        <html>
          <head>
            <title>Asset QR Code</title>
            <style>
              body { 
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                padding: 2rem;
              }
              .qr-container {
                text-align: center;
                padding: 2rem;
                border: 1px solid #ccc;
                border-radius: 8px;
              }
              h2 { margin-bottom: 1rem; }
              p { color: #666; margin-bottom: 2rem; }
            </style>
          </head>
          <body>
            <div class="qr-container">
              <h2>${asset?.name || asset?.asset_name}</h2>
              <p>Asset ID: ${asset?.id}</p>
              <img src="${canvas.toDataURL()}" />
            </div>
          </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.focus();
      printWindow.print();
    }
  };

  const handleDownloadQR = () => {
    const svg = document.getElementById('asset-qr-code');
    if (!svg) return;
    
    // Create a canvas element
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    // Set canvas size to match SVG
    canvas.width = 200;
    canvas.height = 200;
    
    // Create a Blob from the SVG data
    const svgData = new XMLSerializer().serializeToString(svg);
    const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
    
    // Create URL from blob
    const url = URL.createObjectURL(svgBlob);
    
    // Create an image from the SVG
    const img = new Image();
    img.onload = () => {
      // Draw image to canvas
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0);
      
      // Convert canvas to blob
      canvas.toBlob((blob) => {
        if (!blob) return;
        
        // Create download link
        const downloadUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.download = `asset-${asset?.id}-qr.png`;
        link.href = downloadUrl;
        link.click();
        
        // Clean up
        URL.revokeObjectURL(downloadUrl);
      }, 'image/png');
      
      URL.revokeObjectURL(url);
    };
    img.src = url;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[50vh]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-apple-blue"></div>
      </div>
    );
  }

  if (!asset) {
    return (
      <div className="flex flex-col items-center justify-center h-[50vh] space-y-4">
        <h2 className="text-xl font-semibold">{t('assets.notFound')}</h2>
        <AppleButton onClick={() => navigate("/assets")}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          {t('common.back')}
        </AppleButton>
      </div>
    );
  }

  const renderTimelineItem = (record: any) => {
    const getIcon = () => {
      switch (record.type) {
        case 'Maintenance':
          return <Activity className="h-4 w-4" />;
        case 'Incident':
          return <AlertTriangle className="h-4 w-4" />;
        case 'Usage':
          return <Clock className="h-4 w-4" />;
        default:
          return <CheckCircle2 className="h-4 w-4" />;
      }
    };

    return (
      <div key={record.id} className="flex gap-4 pb-6 relative">
        <div className="flex flex-col items-center">
          <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-600">
            {getIcon()}
          </div>
          <div className="flex-1 w-px bg-gray-200 absolute top-8 bottom-0 left-4" />
        </div>
        <div className="flex-1">
          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="flex justify-between items-start mb-2">
              <h4 className="font-medium text-gray-900">{record.title}</h4>
              <span className="text-sm text-gray-500">{formatDate(record.created_at)}</span>
            </div>
            <p className="text-gray-600 text-sm">{record.details}</p>
            {record.cost && (
              <div className="mt-2 flex items-center gap-2 text-sm text-gray-600">
                <DollarSign className="h-4 w-4" />
                Cost: ${record.cost.toLocaleString()}
              </div>
            )}
            {record.status && (
              <Badge className={cn("mt-2", getStatusColor(record.status))}>
                {record.status}
              </Badge>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white p-6 rounded-lg">
        <div className="flex items-center gap-4">
          <AppleButton variant="secondary" onClick={() => navigate("/assets")}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t('common.back')}
          </AppleButton>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">
              {asset.name || asset.asset_name || t('assets.unnamed')}
            </h1>
            {asset.category && (
              <Badge variant="outline" className="mt-2">
                {asset.category}
              </Badge>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <ShareDialog itemId={asset.id} itemType="asset" />
          <AppleButton onClick={() => navigate(`/assets/${asset.id}/edit`)}>
            <Pencil className="h-4 w-4 mr-2" />
            {t('common.edit')}
          </AppleButton>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Info Column */}
        <div className="lg:col-span-2 space-y-6">
          {/* Basic Information */}
          <Card className="p-6 space-y-4 bg-white">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <Info className="h-5 w-5" />
              {t('assetCard.sections.basicInfo')}
            </h2>
            <div className="grid gap-4">
              {asset.asset_description && (
                <p className="text-gray-600">{asset.asset_description}</p>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {asset.status && (
                  <div className="flex items-center gap-2">
                    <Badge className={cn("capitalize", getStatusColor(asset.status))}>
                      {asset.status}
                    </Badge>
                  </div>
                )}
                {asset.asset_price && (
                  <div className="flex items-center gap-2 text-gray-600">
                    <DollarSign className="h-4 w-4" />
                    ${asset.asset_price.toLocaleString()}
                  </div>
                )}
              </div>
            </div>
          </Card>

          {/* Location Information */}
          <Card className="p-6 space-y-4 bg-white">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <MapPin className="h-5 w-5" />
              {t('assetCard.sections.location')}
            </h2>
            <div className="grid gap-4">
              {(asset.company?.name || asset.place?.name) && (
                <div className="flex items-center gap-2 text-gray-600">
                  <Building2 className="h-4 w-4" />
                  <span>
                    {asset.company?.name && asset.place?.name
                      ? `${asset.company.name} - ${asset.place.name}`
                      : asset.company?.name || asset.place?.name}
                  </span>
                </div>
              )}
              {asset.location && (
                <div className="flex items-center gap-2 text-gray-600">
                  <MapPin className="h-4 w-4" />
                  {asset.location}
                </div>
              )}
              {(asset.latitude || asset.longitude) && (
                <div className="flex items-center gap-2 text-gray-600">
                  <MapPin className="h-4 w-4" />
                  <span>
                    {asset.latitude && asset.longitude
                      ? `${asset.latitude.toFixed(6)}, ${asset.longitude.toFixed(6)}`
                      : asset.latitude
                      ? `Lat: ${asset.latitude.toFixed(6)}`
                      : `Long: ${asset.longitude.toFixed(6)}`}
                  </span>
                </div>
              )}
            </div>
          </Card>

          {/* Contact Information */}
          <Card className="p-6 space-y-4 bg-white">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <User className="h-5 w-5" />
              {t('assetCard.sections.contactInfo')}
            </h2>
            <div className="grid gap-4">
              {asset.asset_contact && (
                <div className="flex items-center gap-2 text-gray-600">
                  <User className="h-4 w-4" />
                  {asset.asset_contact}
                </div>
              )}
              {asset.phone && (
                <div className="flex items-center gap-2 text-gray-600">
                  <Phone className="h-4 w-4" />
                  {asset.phone}
                </div>
              )}
            </div>
          </Card>

          {/* Records and Reminders */}
          <Card className="p-6 space-y-4 bg-white">
            <h2 className="text-lg font-semibold">{t('asset.items')}</h2>
            <AssetRecordsList
              assetId={asset.id}
              onRecordAdded={() => {}}
              onReminderAdded={() => {}}
              defaultExpanded={true}
            />
          </Card>
        </div>

        {/* Side Column */}
        <div className="space-y-6">
          {/* Asset Details */}
          <Card className="p-6 space-y-4 bg-white">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <Info className="h-5 w-5" />
              {t('assetCard.sections.assetDetails')}
            </h2>
            <div className="space-y-4">
              {asset.internal_asset_id && (
                <div className="flex items-center gap-2 text-gray-600">
                  <Hash className="h-4 w-4" />
                  <span className="font-medium">ID:</span> {asset.internal_asset_id}
                </div>
              )}
              {asset.serial_number && (
                <div className="flex items-center gap-2 text-gray-600">
                  <Hash className="h-4 w-4" />
                  <span className="font-medium">{t('assetCard.fields.serialNo')}:</span> {asset.serial_number}
                </div>
              )}
              {asset.nfc_id && (
                <div className="flex items-center gap-2 text-gray-600">
                  <Nfc className="h-4 w-4" />
                  <span className="font-medium">NFC ID:</span> {asset.nfc_id}
                </div>
              )}
              {asset.qr_id && (
                <div className="flex items-center gap-2 text-gray-600">
                  <QrCode className="h-4 w-4" />
                  <span className="font-medium">QR ID:</span> {asset.qr_id}
                </div>
              )}
              {asset.code && (
                <div className="flex items-center gap-2 text-gray-600">
                  <Key className="h-4 w-4" />
                  <span className="font-medium">{t('assetCard.fields.code')}:</span> {asset.code}
                </div>
              )}
              {asset.purchase_date && (
                <div className="flex items-center gap-2 text-gray-600">
                  <Calendar className="h-4 w-4" />
                  <span className="font-medium">{t('assetCard.fields.purchase')}:</span>{' '}
                  {formatDate(asset.purchase_date)}
                </div>
              )}
              {asset.warranty_expiry && (
                <div className="flex items-center gap-2 text-gray-600">
                  <Calendar className="h-4 w-4" />
                  <span className="font-medium">{t('assetCard.fields.warranty')}:</span>{' '}
                  {formatDate(asset.warranty_expiry)}
                </div>
              )}
              {asset.rating !== null && asset.rating !== undefined && (
                <div className="flex items-center gap-2 text-gray-600">
                  <Star className="h-4 w-4" />
                  <span className="font-medium">{t('forms.asset.rating')}:</span>{' '}
                  {asset.rating}/5
                </div>
              )}
              {asset.conditions !== null && asset.conditions !== undefined && (
                <div className="flex items-center gap-2 text-gray-600">
                  <Gauge className="h-4 w-4" />
                  <span className="font-medium">{t('forms.asset.conditions')}:</span>{' '}
                  {asset.conditions}/5
                </div>
              )}
            </div>
          </Card>

          {/* Attachments */}
          {(asset.images || asset.documents) && (
            <Card className="p-6 space-y-4 bg-white">
              <h2 className="text-lg font-semibold">{t('assetCard.sections.attachments')}</h2>
              <div className="space-y-6">
                {/* Images */}
                {asset.images && (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <ImageIcon className="h-4 w-4" />
                      <span>
                        {t('records.images')} ({Array.isArray(asset.images) ? asset.images.length : asset.images.split(',').filter(Boolean).length})
                      </span>
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      {(Array.isArray(asset.images) ? asset.images : asset.images.split(',').filter(Boolean)).map((image, index) => (
                        <button
                          key={index}
                          onClick={() => setPreviewState({
                            isOpen: true,
                            type: 'image',
                            url: image,
                            title: `${t('records.preview')} ${index + 1}`
                          })}
                          className="aspect-square rounded-md overflow-hidden border border-gray-200 hover:border-apple-blue transition-colors"
                        >
                          <img
                            src={image}
                            alt={`${t('records.preview')} ${index + 1}`}
                            className="w-full h-full object-cover"
                          />
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Documents */}
                {asset.documents && (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <FileText className="h-4 w-4" />
                      <span>
                        {t('records.documents')} ({Array.isArray(asset.documents) ? asset.documents.length : asset.documents.split(',').filter(Boolean).length})
                      </span>
                    </div>
                    <div className="grid gap-2">
                      {(Array.isArray(asset.documents) ? asset.documents : asset.documents.split(',').filter(Boolean)).map((doc, index) => (
                        <button
                          key={index}
                          onClick={() => setPreviewState({
                            isOpen: true,
                            type: 'document',
                            url: doc,
                            title: `${t('records.document')} ${index + 1}`
                          })}
                          className="flex items-center gap-2 p-2 rounded-md border border-gray-200 hover:border-apple-blue transition-colors text-sm text-gray-600 hover:text-apple-blue"
                        >
                          <FileText className="h-4 w-4" />
                          <span>{t('records.doc')} {index + 1}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </Card>
          )}

          {/* QR Code & NFC Section */}
          <Card className="p-6 space-y-4 bg-white">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <QrCode className="h-5 w-5" />
              {t('asset.identifiers')}
            </h2>
            <div className="space-y-6">
              {/* QR Code */}
              <div className="flex flex-col items-center space-y-4 p-4 border border-gray-200 rounded-lg">
                <QRCodeSVG
                  id="asset-qr-code"
                  value={`${window.location.origin}/scan/${asset?.id}`}
                  size={200}
                  level="H"
                  includeMargin
                />
                <div className="flex gap-2">
                  <AppleButton variant="secondary" size="sm" onClick={handlePrintQR}>
                    <Printer className="h-4 w-4 mr-2" />
                    {t('asset.printQR')}
                  </AppleButton>
                  <AppleButton variant="secondary" size="sm" onClick={handleDownloadQR}>
                    <Download className="h-4 w-4 mr-2" />
                    {t('asset.downloadQR')}
                  </AppleButton>
                </div>
              </div>

              {/* NFC Status */}
              {asset?.nfc_id && (
                <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Nfc className="h-5 w-5 text-gray-600" />
                    <div>
                      <p className="font-medium">{t('asset.nfcTag')}</p>
                      <p className="text-sm text-gray-600">{asset.nfc_id}</p>
                    </div>
                  </div>
                  <Badge variant="outline" className="bg-green-50 text-green-700">
                    {t('asset.tagActive')}
                  </Badge>
                </div>
              )}
            </div>
          </Card>

          {/* Related Assets */}
          {relatedAssets && relatedAssets.length > 0 && (
            <Card className="p-6 space-y-4 bg-white">
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <Network className="h-5 w-5" />
                {t('asset.relatedAssets')}
              </h2>
              <div className="grid gap-4">
                {relatedAssets.map((relatedAsset) => (
                  <button
                    key={relatedAsset.id}
                    onClick={() => navigate(`/assets/${relatedAsset.id}`)}
                    className="flex items-center gap-4 p-4 border border-gray-200 rounded-lg hover:border-apple-blue transition-colors text-left"
                  >
                    {relatedAsset.images && (
                      <img
                        src={relatedAsset.images.split(',')[0]}
                        alt={relatedAsset.name}
                        className="w-12 h-12 rounded-md object-cover"
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-gray-900 truncate">
                        {relatedAsset.name || relatedAsset.asset_name}
                      </h3>
                      <p className="text-sm text-gray-500">{relatedAsset.category}</p>
                    </div>
                    <LinkIcon className="h-4 w-4 text-gray-400" />
                  </button>
                ))}
              </div>
            </Card>
          )}
        </div>
      </div>

      {/* Analytics Card */}
      <Card className="p-6 space-y-6 bg-white">
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <TrendingUp className="h-5 w-5" />
          {t('asset.analytics')}
        </h2>

        {/* Monthly Costs */}
        <div>
          <h3 className="text-md font-medium mb-4 flex items-center gap-2">
            <BarChart3 className="h-4 w-4" />
            {t('asset.costs')}
          </h3>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={analyticsData?.monthlyCosts} margin={{ top: 5, right: 20, bottom: 65, left: 20 }}>
                <defs>
                  <linearGradient id="colorBar" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#0088FE" stopOpacity={0.8}/>
                    <stop offset="95%" stopColor="#0088FE" stopOpacity={0.2}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis 
                  dataKey="name" 
                  angle={-45}
                  textAnchor="end"
                  height={60}
                  tick={{ fontSize: 12 }}
                  interval={0}
                />
                <YAxis 
                  tickFormatter={(value) => `$${value.toLocaleString()}`}
                  tick={{ fontSize: 12 }}
                />
                <Tooltip 
                  formatter={(value: any) => [`$${value.toLocaleString()}`, t('asset.totalCost')]}
                  contentStyle={{ 
                    backgroundColor: 'white', 
                    border: '1px solid #e5e7eb',
                    borderRadius: '6px',
                    padding: '8px'
                  }}
                />
                <Bar 
                  dataKey="value" 
                  fill="url(#colorBar)"
                  radius={[4, 4, 0, 0]}
                  maxBarSize={50}
                  animationDuration={1500}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Status Distribution */}
        <div>
          <h3 className="text-md font-medium mb-4 flex items-center gap-2">
            <PieChart className="h-4 w-4" />
            {t('asset.statusDistribution')}
          </h3>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <RePieChart>
                <Pie
                  data={analyticsData?.statusDistribution}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {analyticsData?.statusDistribution.map((_: any, index: number) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </RePieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </Card>

      {/* Asset Record Attachments - Moved to bottom */}
      <AssetRecordAttachments assetId={parseInt(id || "0")} />

      {/* Preview Dialog */}
      <Dialog open={previewState?.isOpen} onOpenChange={(open) => !open && setPreviewState(null)}>
        <DialogContent className="sm:max-w-[800px] h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>{previewState?.title}</DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto flex items-center justify-center p-4">
            {previewState?.type === 'image' ? (
              <img
                src={previewState.url}
                alt={previewState.title}
                className="max-w-full max-h-full object-contain"
              />
            ) : (
              <iframe
                src={previewState?.url}
                title={previewState?.title}
                className="w-full h-full"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
} 
